import AsyncStorage from '@react-native-async-storage/async-storage'
import type { StoreEnhancer } from '@reduxjs/toolkit'
import { configureStore } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import type { PersistConfig } from 'redux-persist'
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1'
import getStoredStateMigrateV4 from 'redux-persist/lib/integration/getStoredStateMigrateV4'
import createSagaMiddleware from 'redux-saga'

import REDUX_PERSIST from 'APP/Config/ReduxPersist'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { logger } from 'APP/Lib/Datadog'
import { createMiddleware } from 'APP/Lib/VideoSession/middleware'
import { attachEventListeners } from 'APP/Lib/VideoSession/eventListener'
import SessionManager from 'APP/NativeModules/SessionManager'
import rootReducer from 'APP/Redux'
import AppSessionActions from 'APP/Redux/AppSessionRedux'
import rootSaga from 'APP/Sagas'
import type { FeaturesState } from 'APP/Store/Features/types'
import { initializationActions } from 'APP/Store/Initialization'

import type { AccountLinkingState } from './AccountLinking/types'
import type { ActiveMinutesState } from './ActiveMinutes/types'
import type { AppointmentBookingState } from './AppointmentBooking/types'
import type { AppSessionState } from './AppSession/types'
import type { DeeplinkState } from './Deeplink/types'
import type { HabitsState } from './Habits/types'
import type { HistoryState } from './History/types'
import type { LoginState } from './Login/types'
import type { NotificationsState } from './Notifications/types'
import type { PatientState } from './Patient/types'
import type { StartupState } from './Startup/types'
import userMiddleware from './UserMiddleware'
import type { WellnessCenterState } from './WellnessCenter/types'
import type { WellBeingState } from './WellBeingAssessment/types'

/* ------------- Saga Middleware ------------- */
export const sagaMiddleware = createSagaMiddleware({
  onError: (err, errorInfo) => {
    logger.error('Uncaught error in saga', { sagaStack: errorInfo.sagaStack }, err)
    // Note: relies on store being hoisted from below. Sorry not sorry.
    store.dispatch(AppSessionActions.setSystemError(String(err), true))
  },
})

const middleware = [
  sagaMiddleware,
  userMiddleware,
  /* ------------- video sesion kit Middleware ----------- */
  createMiddleware(SessionManager),
]

/* ------------- Logger Middleware ------------- */
const devEnhancers: StoreEnhancer[] = []

if (__DEV__ && !isWeb()) {
  // istanbul ignore next
  const { rozeniteDevToolsEnhancer } = require('@rozenite/redux-devtools-plugin')
  // istanbul ignore next
  devEnhancers.push(rozeniteDevToolsEnhancer())
}

type RootReducerState = ReturnType<typeof rootReducer>

/* ------------- Redux Persist ------------- */
// Reuses the blacklist and transforms from REDUX_PERSIST.storeConfig.
// getStoredStateMigrateV4 imports data written by redux-persist v4 installs
// (one storage record per reducer) into the single persist:root record.
// autoMergeLevel1 restores each persisted reducer's state wholesale, so
// seamless-immutable sub-states are never spread into plain objects.
// timeout: 0 disables the rehydration watchdog; the app gates rendering on
// its own initialization flag, and a timeout would rehydrate an empty state.

// redux-persist's own typings are internally inconsistent around the v4 import:
// V4Config.storage models the v4-era callback API that AsyncStorage no longer
// exposes (getStoredStateMigrateV4 promisifies it at runtime), and the function
// is declared as returning Promise<object | undefined> while
// PersistConfig.getStoredState expects Promise<PersistedState>.
const getStoredState = getStoredStateMigrateV4(
  REDUX_PERSIST.storeConfig as unknown as Parameters<typeof getStoredStateMigrateV4>[0]
) as PersistConfig<RootReducerState>['getStoredState']

const persistConfig: PersistConfig<RootReducerState> = {
  key: 'root',
  storage: AsyncStorage,
  blacklist: REDUX_PERSIST.storeConfig.blacklist,
  transforms: REDUX_PERSIST.storeConfig.transforms,
  stateReconciler: autoMergeLevel1,
  getStoredState,
  version: 1,
  timeout: 0,
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

// Now we're ready to create the store
const store = configureStore({
  reducer: persistedReducer,
  /* ------------- Assemble Middleware ------------- */
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: true,
    }).concat(middleware),
  devTools: __DEV__,

  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(...devEnhancers),
})

attachEventListeners(store, SessionManager)

// Set store in saga context for sagas that need it (e.g., initialization saga for hydration)
sagaMiddleware.setContext({ store })

// kick off root saga
sagaMiddleware.run(rootSaga)

// Start initialization (hydrates store and triggers startup when ready)
store.dispatch(initializationActions.initialize())

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

// NOTE: Partially typed root state to avoid type errors with the reducers formed by reduxsauce
export type BrokenRootState = Omit<
  ReturnType<typeof store.getState>,
  | 'appSession'
  | 'deeplink'
  | 'wellnessCenter'
  | 'habits'
  | 'accountLinking'
  | 'activeMinutes'
  | 'wellBeingAssessment'
  | 'appointmentBooking'
  | 'app'
  | 'login'
  | 'patient'
  | 'history'
  | 'notifications'
  | 'features'
>

type MissingRootState = {
  appSession: AppSessionState
  deeplink: DeeplinkState
  wellnessCenter: WellnessCenterState
  habits: HabitsState
  accountLinking: AccountLinkingState
  activeMinutes: ActiveMinutesState
  wellBeingAssessment: WellBeingState
  appointmentBooking: AppointmentBookingState
  app: StartupState
  login: LoginState
  patient: PatientState
  history: HistoryState
  notifications: NotificationsState
  features: FeaturesState
}

export type RootState = BrokenRootState & MissingRootState

export default store
